// src/forms/ProductForm.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { collection, addDoc, doc, runTransaction, Timestamp } from "firebase/firestore";
import { getUploadErrorMessage, uploadImageToImgbb } from "../utils/uploadImageToImgbb";
import { AGRICULTURAL_CATEGORIES } from "../utils/agriculturalCategories";
import {
  ANIMAL_CATEGORIES,
  ANIMAL_QUANTITY_UNITS,
  isAnimalCategory,
  resolveAnimalCategoryId,
} from "../utils/animalCategories";
import { useProfile } from "../hooks/useProfile";
import {
  calculateExpiration,
  getOptimalStorageCondition,
  getAvailableStatesForCategory,
  PRODUCT_STATES,
  STORAGE_CONDITIONS,
} from "../utils/expirationPredictor";
import {
  CheckCircle2,
  Calendar,
  Tag,
  X,
  ArrowLeft,
  Info,
  Upload,
} from "lucide-react";
import { ImageUploader } from "../components/ProductForm/ImageUploader";
import { ProductDetailsSection } from "../components/ProductForm/ProductDetailsSection";
import { ExpirationSection } from "../components/ProductForm/ExpirationSection";
import { useToast } from "../contexts/ToastContext";
import { awardImpactPoints, IMPACT_ACTIONS } from "../services/impactPointsService";
import { resolveUserDisplayName } from "../utils/userIdentity";
import { Button, Input, ConfirmDialog } from "../components/ui";
import ErrorModal from "../components/ErrorModal";
import { canUsePreferencesStorage } from "../utils/storageConsent";
import { errorDev } from '../utils/logger.js';

// Helper functions
async function compressImage(file, { maxW = 1600, maxH = 1600, quality = 0.7 } = {}) {
  if (!file.type.startsWith("image/")) return file;

  const dataUrl = await new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });

  const img = await new Promise((res) => {
    const i = new Image();
    i.onload = () => res(i);
    i.src = dataUrl;
  });

  const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * ratio);
  canvas.height = Math.round(img.height * ratio);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise((res) =>
    canvas.toBlob(res, "image/jpeg", quality)
  );
  return new File([blob], (file.name || "image").replace(/\.\w+$/, ".jpg"), {
    type: "image/jpeg",
  });
}

function getCategoryIdFromName(categoryName) {
  if (!categoryName) return null;
  const byId = AGRICULTURAL_CATEGORIES.find(
    (c) => c.id.toLowerCase() === String(categoryName).toLowerCase()
  );
  if (byId) return byId.id;

  const cat = AGRICULTURAL_CATEGORIES.find(
    (c) => c.name.toLowerCase() === String(categoryName).toLowerCase()
  );
  return cat ? cat.id : null;
}

function getProductStatesForCategory(categoryName) {
  const categoryId = getCategoryIdFromName(categoryName);
  if (!categoryId) return [PRODUCT_STATES.FRESH];
  return getAvailableStatesForCategory(categoryId);
}

function isAnimalCategorySelection(categoryValue) {
  if (!categoryValue) return false;
  return isAnimalCategory(categoryValue);
}

function getAnimalCategoryConfig(categoryValue) {
  if (!categoryValue) return null;
  return resolveAnimalCategoryId(categoryValue)
    ? ANIMAL_CATEGORIES.find((c) => c.id === resolveAnimalCategoryId(categoryValue))
    : null;
}

function getQuantityUnitForCategory(categoryValue) {
  const animal = getAnimalCategoryConfig(categoryValue);
  if (animal) return animal.quantityUnit;
  return "kg";
}

function requiresExpiryForCategory(categoryValue) {
  const animal = getAnimalCategoryConfig(categoryValue);
  if (animal) return animal.requiresExpiry;
  return true;
}

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

export default function ProductForm({ onSuccess }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["product", "farmMarketplace", "common", "cropCatalog"]);
  const toast = useToast();
  const { user } = useAuth();
  const { profile: userProfile } = useProfile(user?.uid);

  const [form, setForm] = useState({
    title: "",
    desc: "",
    price: "",
    quantity: "",
    category: "",
    productState: PRODUCT_STATES.FRESH,
    province: "",
    district: "",
    sector: "",
    cell: "",
    expiryDate: "",
    isPrivate: false,
    quantityUnit: "kg",
    unitType: "kg",
    traySize: "",
    collectionDate: "",
    productType: "crop",
  });

  const [images, setImages] = useState([]);
  const [localFiles, setLocalFiles] = useState([]);
  const [previewURLs, setPreviewURLs] = useState([]);
  const [coverIndex, setCoverIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(null);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [cropSuggestions, setCropSuggestions] = useState([]);
  const [productStateOptions, setProductStateOptions] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showCropDropdown, setShowCropDropdown] = useState(false);
  const [storageCondition, setStorageCondition] = useState(STORAGE_CONDITIONS.AMBIENT);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [showPricingGuide, setShowPricingGuide] = useState(false);

  const MAX_IMAGES = 3;
  const draftKey = "productFormDraftV3";
  const categoryDropdownRef = useRef(null);
  const submitLockRef = useRef(false);
  const submitRequestIdRef = useRef(null);

  // Load categories
  useEffect(() => {
    const localizedCategories = AGRICULTURAL_CATEGORIES.map((cat) => ({
      value: cat.id,
      label: t(`cropCatalog:categories.${cat.id}`, cat.name),
    }));
    const localizedAnimalCategories = ANIMAL_CATEGORIES.map((cat) => ({
      value: cat.id,
      label: t(`product:form.animal.categories.${cat.id}`, cat.name),
    }));
    setCategoryOptions([...localizedCategories, ...localizedAnimalCategories]);
  }, [i18n.language, t]);

  // Load profile location
  useEffect(() => {
    if (!userProfile) return;
    const { location } = userProfile;
    if (location) {
      setForm((prev) => ({
        ...prev,
        province: prev.province || (typeof location === "string" ? "" : location.province) || "",
        district: prev.district || (typeof location === "string" ? "" : location.district) || "",
        sector: prev.sector || (typeof location === "string" ? "" : location.sector) || "",
        cell: prev.cell || (typeof location === "string" ? "" : location.cell) || "",
      }));
    }
  }, [userProfile]);

  // Update crop suggestions and product states
  useEffect(() => {
    if (!form.category) {
      setCropSuggestions([]);
      setProductStateOptions([]);
      return;
    }

    // Animal categories have their own suggestion list and do not use crop states.
    if (isAnimalCategorySelection(form.category)) {
      const animal = getAnimalCategoryConfig(form.category);
      setCropSuggestions(animal ? (animal.examples || []) : []);
      setProductStateOptions([PRODUCT_STATES.FRESH]);
      return;
    }

    const selectedCat = AGRICULTURAL_CATEGORIES.find(
      (cat) => cat.id === getCategoryIdFromName(form.category)
    );
    if (selectedCat) {
      const translatedCrops = t(`cropCatalog:crops.${selectedCat.id}`, {
        returnObjects: true,
        defaultValue: selectedCat.examples || [],
      });
      setCropSuggestions(Array.isArray(translatedCrops) ? translatedCrops : selectedCat.examples || []);
      const states = getProductStatesForCategory(form.category);
      setProductStateOptions(states);
      if (!form.productState || !states.includes(form.productState)) {
        setForm((prev) => ({
          ...prev,
          productState: states[0] || PRODUCT_STATES.FRESH,
        }));
      }
    }
  }, [form.category, i18n.language, t]);

  // Auto-calculate expiration and storage
  useEffect(() => {
    if (!form.category || !form.productState) return;
    const categoryId = getCategoryIdFromName(form.category);
    if (!categoryId) return;

    // Animal categories do not use crop shelf-life prediction.
    if (isAnimalCategorySelection(form.category)) return;

    const optimalStorage = getOptimalStorageCondition(categoryId, form.productState);
    setStorageCondition(optimalStorage);

    try {
      const result = calculateExpiration({
        category: categoryId,
        productionDate: new Date(),
        productState: form.productState,
        storageCondition: optimalStorage,
      });

      if (result.success && result.expirationDate) {
        setForm((prev) => ({
          ...prev,
          expiryDate: prev.expiryDate || result.expirationDate,
        }));
      }
    } catch (err) {
      // Non-critical: keep manual expiry flow available when prediction fails.
    errorDev("Operation failed:", err);
    }
  }, [form.category, form.productState]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Draft save/load
  useEffect(() => {
    if (!canUsePreferencesStorage()) return;
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        setForm((prev) => ({ ...prev, ...parsed, isPrivate: false }));
      }
    } catch (err) { errorDev("Operation failed:", err);}
  }, []);

  useEffect(() => {
    if (!canUsePreferencesStorage()) return;
    const toSave = { ...form };
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify(toSave));
      } catch (err) { errorDev("Operation failed:", err);}
    }, 250);
    return () => clearTimeout(timer);
  }, [form]);

  const buildPostFingerprint = (uid, payload) => {
    const normalized = [
      uid || "",
      (payload.title || "").trim().toLowerCase(),
      String(payload.price ?? "").trim(),
      String(payload.quantity ?? "").trim(),
      (payload.category || "").trim().toLowerCase(),
      (payload.productState || "").trim().toLowerCase(),
      String(payload.expiryDate || "").trim(),
      payload.isPrivate ? "1" : "0",
      String(previewURLs.length),
    ];
    return normalized.join("|");
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setSubmitError(null);
  };

  const processLocalFiles = (files) => {
    const imgs = files.filter((f) => f.type?.startsWith("image/"));
    const invalidCount = files.length - imgs.length;
    if (!imgs.length) {
      setImageUploadError(t("product:form.errors.invalidFileType", "Please select valid image files only."));
      return;
    }

    const available = MAX_IMAGES - previewURLs.length;
    const toAdd = imgs.slice(0, Math.max(0, available));

    if (toAdd.length < imgs.length) {
      setImageUploadError(
        t("product:form.errors.maxImages", {
          max: MAX_IMAGES,
          added: toAdd.length,
          total: imgs.length,
          defaultValue: `Maximum ${MAX_IMAGES} images allowed. Added ${toAdd.length} of ${imgs.length}.`,
        })
      );
      setTimeout(() => setImageUploadError(null), 3000);
    }

    if (invalidCount > 0) {
      setImageUploadError(t("product:form.errors.invalidFileType", "Please select valid image files only."));
    }

    const previews = toAdd.map((f) => URL.createObjectURL(f));
    setLocalFiles((p) => [...p, ...toAdd]);
    setPreviewURLs((p) => [...p, ...previews]);
    setSubmitError(null);
  };

  const removeImage = (index) => {
    // Revoke the object URL to prevent memory leaks
    if (previewURLs[index]) {
      URL.revokeObjectURL(previewURLs[index]);
    }
    
    setPreviewURLs((prev) => prev.filter((_, i) => i !== index));
    setLocalFiles((prev) => prev.filter((_, i) => i !== index));
    setImages((prev) => prev.filter((_, i) => i !== index));
    if (coverIndex === index) setCoverIndex(0);
    else if (index < coverIndex) setCoverIndex((c) => Math.max(0, c - 1));
  };

  const moveImage = (from, to) => {
    if (to < 0 || to >= previewURLs.length) return;
    const reorder = (arr) => {
      const copy = [...arr];
      const [item] = copy.splice(from, 1);
      copy.splice(to, 0, item);
      return copy;
    };
    setPreviewURLs((p) => reorder(p));
    setLocalFiles((p) => reorder(p));
    setImages((p) => reorder(p));
    setCoverIndex((c) => (c === from ? to : c));
  };

  const uploadAllImages = async () => {
    const needUploadCount = previewURLs.length - images.length;
    if (needUploadCount <= 0) return images;

    setUploading(true);
    setUploadProgress({ done: 0, total: needUploadCount });
    setSubmitError(null);

    const uploaded = [...images];
    const toUpload = localFiles.slice(images.length);
    let uploadErrors = [];

    for (let i = 0; i < toUpload.length; i++) {
      const original = toUpload[i];
      try {
        const compressed = await compressImage(original, {
          maxW: 1600,
          maxH: 1600,
          quality: 0.7,
        });
        const url = await uploadImageToImgbb(compressed, { target: "product" });
        if (url) {
          uploaded.push(url);
          setImages([...uploaded]);
        } else {
          uploadErrors.push(
            t("product:form.errors.imageNoUrl", {
              index: i + 1,
              defaultValue: `Image ${i + 1}: No URL returned`,
            })
          );
        }
      } catch (e) {
        uploadErrors.push(
          t("product:form.errors.imageUploadItem", {
            index: i + 1,
            error: getUploadErrorMessage(e),
            defaultValue: `Image ${i + 1}: ${getUploadErrorMessage(e)}`,
          })
        );
      } finally {
        setUploadProgress((p) => ({ ...p, done: p.done + 1 }));
      }
    }

    setUploading(false);
    
    if (uploadErrors.length > 0 && uploaded.length === 0) {
      setSubmitError(
        t("product:form.errors.imageUploadFailed", {
          errors: uploadErrors.join(", "),
          defaultValue: `Image upload failed: ${uploadErrors.join(", ")}`,
        })
      );
      return [];
    } else if (uploadErrors.length > 0) {
      setSubmitError(
        t("product:form.errors.someImagesFailed", {
          errors: uploadErrors.join(", "),
          successCount: uploaded.length,
          defaultValue: `Some images failed to upload: ${uploadErrors.join(", ")}. ${uploaded.length} images uploaded successfully.`,
        })
      );
    }
    
    return uploaded;
  };

const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current || isSubmitting || uploading || submitSuccess) return;

    submitLockRef.current = true;
    setIsSubmitting(true);

    setSubmitError(null);
    setSubmitSuccess(false);
    setImageUploadError(null);

    const { title, price, category, productState, expiryDate } = form;
    const animalConfig = getAnimalCategoryConfig(category);
    const isAnimal = isAnimalCategorySelection(category);
    const expiryRequired = animalConfig ? animalConfig.requiresExpiry : true;

    if (!user) {
      setSubmitError(t("farmMarketplace:loginRequired", "Please log in to continue."));
      submitLockRef.current = false;
      setIsSubmitting(false);
      return;
    }

    if (!title.trim() || !price || previewURLs.length === 0 || !category || !productState) {
      setSubmitError(t("product:form.errors.requiredFields", "Please fill all required fields and add at least 1 image."));
      submitLockRef.current = false;
      setIsSubmitting(false);
      return;
    }

    // Expiry is required for crops; optional for live animals; for eggs we
    // accept either an expiry date or a best-before date.
    if (expiryRequired) {
      if (!expiryDate) {
        setSubmitError(t("product:form.errors.requiredFields", "Please fill all required fields and add at least 1 image."));
        submitLockRef.current = false;
        setIsSubmitting(false);
        return;
      }
      if (new Date(expiryDate) <= new Date()) {
        setSubmitError(t("product:form.errors.expiryFuture", "Expiry date must be in the future."));
        submitLockRef.current = false;
        setIsSubmitting(false);
        return;
      }
    } else if (expiryDate && new Date(expiryDate) <= new Date()) {
      setSubmitError(t("product:form.errors.expiryFuture", "Expiry date must be in the future."));
      submitLockRef.current = false;
      setIsSubmitting(false);
      return;
    }

    // For eggs sold by tray, require a tray size so totals can be calculated.
    if (isAnimal && form.unitType === ANIMAL_QUANTITY_UNITS.TRAY) {
      const traySize = Number(form.traySize);
      if (!Number.isFinite(traySize) || traySize <= 0) {
        setSubmitError(t("product:form.errors.traySizeRequired", "Please enter a valid tray size (number of eggs per tray)."));
        submitLockRef.current = false;
        setIsSubmitting(false);
        return;
      }
    }

    const urls = await uploadAllImages();
    if (urls.length === 0) {
      setSubmitError(t("product:form.errors.uploadRetry", "It's Orrith, not you - please retry your upload."));
      submitLockRef.current = false;
      setIsSubmitting(false);
      return;
    }

    try {
      const ownerName = resolveUserDisplayName(userProfile || user, user?.email);
      const requestId =
        submitRequestIdRef.current ||
        (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${user.uid}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
      submitRequestIdRef.current = requestId;

      const selectedCategory = AGRICULTURAL_CATEGORIES.find((c) => c.id === form.category);
      const categoryLabel = selectedCategory
        ? t(`cropCatalog:categories.${selectedCategory.id}`, selectedCategory.name)
        : t(`product:form.animal.categories.${form.category}`, form.category);

      const quantityValue = Number(form.quantity) || 0;
      const unitType = isAnimal ? form.unitType : "kg";
      const quantityUnit = isAnimal ? unitType : "kg";
      const traySize = form.unitType === ANIMAL_QUANTITY_UNITS.TRAY ? Number(form.traySize) || null : null;

      const productDoc = {
        title: form.title.trim(),
        name: form.title.trim(),
        description: form.desc.trim(),
        price: Number(form.price),
        // Preserve pricePerKg for old consumers and marketplace calculations.
        pricePerKg: Number(form.price),
        quantity: quantityValue,
        quantityUnit: quantityUnit,
        unitType: unitType,
        categoryId: form.category,
        category: selectedCategory?.name || form.category,
        categoryLabel,
        productType: isAnimal ? "animal" : "crop",
        productState: form.productState,
        storageCondition: storageCondition,
        images: urls,
        coverIndex,
        expiry: expiryDate ? Timestamp.fromDate(new Date(expiryDate)) : null,
        collectionDate: form.collectionDate ? Timestamp.fromDate(new Date(form.collectionDate)) : null,
        bestBefore: form.bestBefore ? Timestamp.fromDate(new Date(form.bestBefore)) : null,
        traySize,
        poster: {
          userId: user.uid,
          username: userProfile?.username || ownerName,
          name: ownerName,
          displayName: ownerName,
          email: user.email,
          photoURL: userProfile?.photoURL || userProfile?.avatar || user.photoURL || null,
        },
        userId: user.uid,
        createdAt: Timestamp.now(),
        sold: false,
        buyerId: null,
        isPrivate: false,
        hidden: false,
        isHidden: false,
        location: {
          province: form.province || "",
          district: form.district || "",
          sector: form.sector || "",
          cell: form.cell || "",
        },
        locationString: [form.province, form.district, form.sector, form.cell]
          .filter(Boolean)
          .join(" / "),
      };

      const productRef = doc(collection(db, "products"));
      const userRef = doc(db, "users", user.uid);
      const dedupeFingerprint = buildPostFingerprint(user.uid, form);

      const txResult = await runTransaction(db, async (tx) => {
        const userSnap = await tx.get(userRef);
        const guard = userSnap.exists() ? userSnap.data()?.productPostGuard || {} : {};

        if (guard.lastRequestId === requestId && typeof guard.lastProductId === "string") {
          return { created: false, productId: guard.lastProductId };
        }

        const lastSubmittedAtMs =
          typeof guard?.lastSubmittedAt?.toMillis === "function"
            ? guard.lastSubmittedAt.toMillis()
            : 0;
        const isLikelyDuplicate =
          guard.lastFingerprint === dedupeFingerprint &&
          lastSubmittedAtMs > 0 &&
          Date.now() - lastSubmittedAtMs < 90 * 1000;

        if (isLikelyDuplicate) {
          throw new Error("duplicate_post_guard");
        }

        tx.set(productRef, productDoc);
        tx.set(
          userRef,
          {
            productPostGuard: {
              lastRequestId: requestId,
              lastProductId: productRef.id,
              lastFingerprint: dedupeFingerprint,
              lastSubmittedAt: Timestamp.now(),
            },
          },
          { merge: true }
        );

        return { created: true, productId: productRef.id };
      });

      // Award Impact Points for product listing (idempotent: only on create, not edit)
      if (txResult.created) {
        Promise.allSettled([
          awardImpactPoints({
            userId: user.uid,
            action: IMPACT_ACTIONS.PRODUCT_POSTED,
            metadata: { productId: txResult.productId, title: form.title, category: form.category },
          }),
          addDoc(collection(db, "impactFeed"), {
            user: user.uid,
            userName: ownerName,
            type: "product_listing",
            timestamp: Timestamp.now(),
            impactData: { productId: txResult.productId, title: form.title, category: form.category },
          }),
        ]).catch(() => {
          // Non-critical side effects should not block successful posting.
        });
      }

      setSubmitSuccess(true);
      try {
        localStorage.removeItem(draftKey);
      } catch (err) { errorDev("Operation failed:", err);}

      // Clean up object URLs
      previewURLs.forEach(url => URL.revokeObjectURL(url));

      setForm({
        title: "",
        desc: "",
        price: "",
        quantity: "",
        category: "",
        productState: PRODUCT_STATES.FRESH,
        province: "",
        district: "",
        sector: "",
        cell: "",
        expiryDate: "",
        isPrivate: false,
        quantityUnit: "kg",
        unitType: "kg",
        traySize: "",
        collectionDate: "",
        productType: "crop",
      });
      setImages([]);
      setLocalFiles([]);
      setPreviewURLs([]);
      setCoverIndex(0);
      setUploadProgress({ done: 0, total: 0 });
      setProductStateOptions([]);
      setStorageCondition(STORAGE_CONDITIONS.AMBIENT);

      if (onSuccess) onSuccess();
      navigate("/farm");
    } catch (err) {
      if (err?.message === "duplicate_post_guard") {
        setSubmitError(
          t(
            "product:form.errors.duplicateGuard",
            "This post is already being processed. Please wait a moment before posting again."
          )
        );
      } else {
        setSubmitError(t("product:form.errors.postFailed", "Failed to post product. Please try again."));
      }
    } finally {
      submitLockRef.current = false;
      submitRequestIdRef.current = null;
      setIsSubmitting(false);
    }
  };

  const pricePerKgLabel = useMemo(() => {
    const n = Number(form.price);
    if (!Number.isFinite(n)) return "";
    const unitKey = form.unitType || "kg";
    const unitLabel =
      unitKey === ANIMAL_QUANTITY_UNITS.BIRD
        ? t("product:form.animal.units.perBird", "Per bird")
        : unitKey === ANIMAL_QUANTITY_UNITS.EGG
        ? t("product:form.animal.units.perEgg", "Per egg")
        : unitKey === ANIMAL_QUANTITY_UNITS.TRAY
        ? t("product:form.animal.units.perTray", "Per tray")
        : "RWF / KG";
    if (unitKey === "kg") {
      return n.toLocaleString("en-US") + " RWF / KG";
    }
    return n.toLocaleString("en-US") + " RWF " + unitLabel;
  }, [form.price, form.unitType, t]);

  const totalPricePreview = useMemo(() => {
    const unitPrice = Number(form.price);
    const quantity = Number(form.quantity);
    if (!Number.isFinite(unitPrice) || !Number.isFinite(quantity) || unitPrice < 0 || quantity <= 0) {
      return null;
    }
    return Math.round(unitPrice * quantity);
  }, [form.price, form.quantity]);

  const handleCategorySelect = (catId) => {
    const animal = getAnimalCategoryConfig(catId);
    setForm((prev) => ({
      ...prev,
      category: catId,
      title: "",
      productState: PRODUCT_STATES.FRESH,
      quantityUnit: animal ? animal.quantityUnit : "kg",
      unitType: animal ? animal.quantityUnit : "kg",
      traySize: animal ? prev.traySize : "",
      collectionDate: animal ? prev.collectionDate : "",
      productType: animal ? "animal" : "crop",
    }));
  };

  useEffect(() => {
    setErrorDialogOpen(Boolean(submitError));
  }, [submitError]);

  const selectedCategoryLabel =
    categoryOptions.find((c) => c.value === form.category)?.label || form.category;

  const productDetailsLabels = {
    sectionTitle: null,
    categoryLabel: t("product:form.details.categoryLabel", "Category"),
    categoryPlaceholder: t("product:form.details.categoryPlaceholder", "Select a category..."),
    productNameLabel: t("product:form.details.productNameLabel", "Product Name"),
    productNamePlaceholder: t("product:form.details.productNamePlaceholder", "Search or enter crop name..."),
    selectCategoryFirst: t("product:form.details.selectCategoryFirst", "Select category first"),
    productStateLabel: t("product:form.details.productStateLabel", "State"),
    storageConditionLabel: t("product:form.details.storageConditionLabel", "Storage Condition"),
    suggestedStorage: t("product:form.details.suggestedStorage", "Suggested"),
    descriptionLabel: t("product:form.details.descriptionLabel", "Description"),
    descriptionPlaceholder: t("product:form.details.descriptionPlaceholder", "Add details about the product..."),
    storageAmbient: t("farmMarketplace:storageCondition.ambient", "Ambient"),
    storageRefrigerated: t("farmMarketplace:storageCondition.refrigerated", "Refrigerated"),
    storageFrozen: t("farmMarketplace:storageCondition.frozen", "Frozen"),
    storageControlledAtmosphere: t("farmMarketplace:storageCondition.controlled_atmosphere", "Controlled Atmosphere"),
    storageVacuumSealed: t("farmMarketplace:storageCondition.vacuum_sealed", "Vacuum Sealed"),
    storageModifiedAtmosphere: t("farmMarketplace:storageCondition.modified_atmosphere", "Modified Atmosphere"),
    storageDryStorage: t("farmMarketplace:storageCondition.dry_storage", "Dry Storage"),
    availableFor: t("product:form.details.availableFor", "Available for"),
    selectedCategory: t("product:form.details.selectedCategory", "selected category"),
    animalCategories: {
      chickens: t("product:form.animal.categories.chickens", "Chickens"),
      eggs: t("product:form.animal.categories.eggs", "Eggs"),
    },
    animalExamples: {
      chickens: t("product:form.animal.examples.chickens", "Kuroiler, Broiler, Layer, Rooster, Hen"),
      eggs: t("product:form.animal.examples.eggs", "Chicken Eggs, Duck Eggs, Turkey Eggs"),
    },
    animalUnits: {
      bird: t("product:form.animal.units.bird", "Bird"),
      egg: t("product:form.animal.units.egg", "Egg"),
      tray: t("product:form.animal.units.tray", "Tray"),
      traySize: t("product:form.animal.units.traySize", "Tray size (eggs)"),
      perBird: t("product:form.animal.units.perBird", "Per bird"),
      perEgg: t("product:form.animal.units.perEgg", "Per egg"),
      perTray: t("product:form.animal.units.perTray", "Per tray"),
    },
    productStateMap: {
      fresh: t("farmMarketplace:productState.fresh", "Fresh"),
      dried: t("farmMarketplace:productState.dried", "Dried"),
      canned: t("farmMarketplace:productState.canned", "Canned"),
      frozen: t("farmMarketplace:productState.frozen", "Frozen"),
      processed: t("farmMarketplace:productState.processed", "Processed"),
      powdered: t("farmMarketplace:productState.powdered", "Powdered"),
      oil: t("farmMarketplace:productState.oil", "Oil"),
      extract: t("farmMarketplace:productState.extract", "Extract"),
    },
  };

  const imageUploaderLabels = {
    addUpTo: t("product:form.images.addUpTo", "Add up to"),
    photos: t("product:form.images.photos", "photos"),
    dragDrop: t("product:form.images.dragDrop", "Drag and drop or tap to upload"),
    selectPhotos: t("product:form.images.selectPhotos", "Select Photos"),
    cover: t("product:form.images.cover", "Cover"),
    moveLeft: t("product:form.images.moveLeft", "Move left"),
    moveRight: t("product:form.images.moveRight", "Move right"),
    setAsCover: t("product:form.images.setAsCover", "Set as cover"),
    remove: t("product:form.images.remove", "Remove"),
    addMorePhotos: t("product:form.images.addMorePhotos", "Add More Photos"),
    uploading: t("product:form.images.uploading", "Uploading"),
  };

  const expirationLabels = {
    sectionTitle: t("product:form.expiration.sectionTitle", "Expiration and Storage"),
    expiryDateLabel: t("product:form.expiration.expiryDateLabel", "Expiry Date"),
    suggestedBasedOn: t("product:form.expiration.suggestedBasedOn", "Suggested based on"),
    smartTitle: t("product:form.expiration.smartTitle", "Smart Auto-Management:"),
    smartDescription: t("product:form.expiration.smartDescription", "Based on your selections, this product has been configured for optimal shelf life."),
    productStateText: t("product:form.expiration.productState", "Product state"),
    storageText: t("product:form.expiration.storage", "Storage"),
    liveAnimalHint: t("product:form.animal.dates.liveAnimalHint", "Live animals do not require a food expiry date."),
    liveAnimalSubhint: t("product:form.animal.dates.liveAnimalSubhint", "You may still record a seller-provided availability note in the description."),
    collectionDateLabel: t("product:form.animal.dates.collectionDateLabel", "Collection date"),
    collectionDateHint: t("product:form.animal.dates.collectionDateHint", "When the eggs were collected"),
    bestBeforeLabel: t("product:form.animal.dates.bestBeforeLabel", "Best before"),
    bestBeforeHint: t("product:form.animal.dates.bestBeforeHint", "Seller-provided best-before date (not a scientific shelf-life prediction)"),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-neutral-900 dark:to-neutral-950 text-gray-900 dark:text-gray-100">
      <div className="max-w-2xl mx-auto px-4 py-4 sm:py-6 pb-20 space-y-6">
        {/* Mobile header with close button */}
        <div className="flex items-center justify-between sm:hidden -mx-4 px-4 py-3 border-b border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800/50 sticky top-0 z-10">
          <button
            type="button"
            onClick={() => navigate("/farm")}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-700 dark:text-gray-300 transition-colors"
            aria-label={t("product:cancel", "Cancel")}
          >
            <X size={20} />
          </button>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t("product:form.post.title", "List Product")}
          </h1>
          <div className="w-8" />
        </div>
        <form
          onSubmit={handleSubmit}
          className="space-y-6"
        >
        {/* Success Alert */}
        {submitSuccess && (
          <div className="flex gap-3 p-3 sm:p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                {t("product:form.success.posted", "Product posted successfully!")}
              </p>
            </div>
          </div>
        )}

        {/* Images Section */}
        <div className="bg-white dark:bg-neutral-800/80 rounded-xl p-4 sm:p-6 shadow-md dark:shadow-lg border border-gray-200 dark:border-neutral-700/50">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t("product:form.images.title", "Photos")}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t("product:form.images.uploadedCount", {
                    count: previewURLs.length,
                    max: MAX_IMAGES,
                    defaultValue: `${previewURLs.length}/${MAX_IMAGES} uploaded`,
                  })}
                </p>
              </div>
            </div>
            {previewURLs.length > 0 && (
              <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-full">
                <p className="text-xs font-semibold text-green-700 dark:text-green-400">
                  {previewURLs.length === MAX_IMAGES
                    ? t("product:form.images.complete", "Complete")
                    : t("product:form.images.more", {
                        count: MAX_IMAGES - previewURLs.length,
                        defaultValue: `${MAX_IMAGES - previewURLs.length} more`,
                      })}
                </p>
              </div>
            )}
          </div>

          <ImageUploader
            images={images}
            previewURLs={previewURLs}
            coverIndex={coverIndex}
            onAddImages={processLocalFiles}
            onRemoveImage={removeImage}
            onMoveImage={moveImage}
            onSetCover={setCoverIndex}
            maxImages={MAX_IMAGES}
            uploading={uploading}
            uploadProgress={uploadProgress}
            error={imageUploadError}
            labels={imageUploaderLabels}
          />
        </div>

        {/* Product Details */}
        <ProductDetailsSection
          form={form}
          categoryOptions={categoryOptions}
          cropSuggestions={cropSuggestions}
          productStateOptions={productStateOptions}
          showCategoryDropdown={showCategoryDropdown}
          showCropDropdown={showCropDropdown}
          storageCondition={storageCondition}
          onInputChange={handleInputChange}
          onCategorySelect={handleCategorySelect}
          setShowCategoryDropdown={setShowCategoryDropdown}
          setShowCropDropdown={setShowCropDropdown}
          setStorageCondition={setStorageCondition}
          categoryDropdownRef={categoryDropdownRef}
          labels={productDetailsLabels}
        />

        {/* Pricing & Quantity */}
        <div className="bg-white dark:bg-neutral-800/80 rounded-xl p-4 sm:p-6 shadow-md dark:shadow-lg border border-gray-200 dark:border-neutral-700/50 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Tag className="w-5 h-5 text-green-600" />
              {t("product:form.pricing.title", "Pricing")}
            </h2>
            <button
              type="button"
              onClick={() => setShowPricingGuide(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition"
            >
              <Info className="w-3.5 h-3.5" />
              {t("product:form.pricing.guideCta", "Pricing guide")}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:items-center">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 md:col-span-3">
              {isAnimalCategorySelection(form.category)
                ? (form.unitType === ANIMAL_QUANTITY_UNITS.BIRD
                    ? t("product:form.animal.units.perBird", "Per bird")
                    : form.unitType === ANIMAL_QUANTITY_UNITS.EGG
                    ? t("product:form.animal.units.perEgg", "Per egg")
                    : t("product:form.animal.units.perTray", "Per tray"))
                : t("product:form.pricing.priceLabel", "Price per kg")}
            </label>
            <div className="md:col-span-4">
              <Input
                type="number"
                name="price"
                value={form.price}
                onChange={handleInputChange}
                required
                min="0"
                placeholder={t("product:form.pricing.pricePlaceholder", "0")}
              />
              {pricePerKgLabel && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{pricePerKgLabel}</p>
              )}
            </div>

            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 md:col-span-2">
              {isAnimalCategorySelection(form.category)
                ? (form.unitType === ANIMAL_QUANTITY_UNITS.BIRD
                    ? t("product:form.animal.units.bird", "Bird")
                    : form.unitType === ANIMAL_QUANTITY_UNITS.EGG
                    ? t("product:form.animal.units.egg", "Egg")
                    : t("product:form.animal.units.tray", "Tray"))
                : t("product:form.pricing.quantityLabel", "Kg amount")}
            </label>
            <div className="md:col-span-3">
              <Input
                type="number"
                name="quantity"
                value={form.quantity}
                onChange={handleInputChange}
                min="0"
                placeholder={isAnimalCategorySelection(form.category)
                  ? (form.unitType === ANIMAL_QUANTITY_UNITS.BIRD
                      ? "Number of birds"
                      : form.unitType === ANIMAL_QUANTITY_UNITS.EGG
                      ? "Number of eggs"
                      : "Number of trays")
                  : t("product:form.pricing.quantityPlaceholder", "Amount in kilograms")}
              />
            </div>
          </div>

          {/* Animal unit selector for eggs (bird/egg/tray) */}
          {isAnimalCategorySelection(form.category) && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:items-center">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 md:col-span-3">
                {t("product:form.animal.units.unitType", "Unit type")}
              </label>
              <div className="md:col-span-9">
                <select
                  name="unitType"
                  value={form.unitType}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-500"
                >
                  <option value={ANIMAL_QUANTITY_UNITS.BIRD}>{t("product:form.animal.units.bird", "Bird")}</option>
                  <option value={ANIMAL_QUANTITY_UNITS.EGG}>{t("product:form.animal.units.egg", "Egg")}</option>
                  <option value={ANIMAL_QUANTITY_UNITS.TRAY}>{t("product:form.animal.units.tray", "Tray")}</option>
                </select>
              </div>
            </div>
          )}

          {/* Tray size — only when eggs are sold by tray */}
          {isAnimalCategorySelection(form.category) && form.unitType === ANIMAL_QUANTITY_UNITS.TRAY && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:items-center">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 md:col-span-3">
                {t("product:form.animal.units.traySize", "Tray size (eggs)")}
              </label>
              <div className="md:col-span-9">
                <Input
                  type="number"
                  name="traySize"
                  value={form.traySize}
                  onChange={handleInputChange}
                  min="1"
                  placeholder="e.g. 30"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t("product:form.animal.units.traySizeHint", "How many eggs fit in one tray. Total eggs = quantity × tray size.")}
                </p>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-3.5 py-3 text-sm text-green-800 dark:text-green-200">
            {totalPricePreview == null ? (
              <span>
                {t("product:form.pricing.totalPreviewEmpty", "Total price that will appear on product card: Enter price per kg and kg amount")}
              </span>
            ) : (
              <span>
                {t("product:form.pricing.totalPreview", "Total price that will appear on product card")}: <strong>{totalPricePreview.toLocaleString("en-US")} RWF</strong>
              </span>
            )}
          </div>
        </div>

        {/* Expiration & Auto-Management */}
        <ExpirationSection
          category={form.category}
          categoryLabel={selectedCategoryLabel}
          productState={form.productState}
          expiryDate={form.expiryDate}
          storageCondition={storageCondition}
          onExpiryDateChange={handleInputChange}
          onCollectionDateChange={handleInputChange}
          onBestBeforeChange={handleInputChange}
          minDate={todayISO()}
          collectionDate={form.collectionDate}
          bestBefore={form.bestBefore}
          isAnimalCategory={isAnimalCategorySelection(form.category)}
          animalConfig={getAnimalCategoryConfig(form.category)}
          labels={expirationLabels}
        />

        {/* Location */}
        <div className="bg-white dark:bg-neutral-800/80 rounded-xl p-4 sm:p-6 shadow-md dark:shadow-lg border border-gray-200 dark:border-neutral-700/50 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t("product:form.location.title", "Location")}</h2>
            {(form.province || form.district) && (
              <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                {t("product:form.location.autoFilled", "Auto-filled from profile")}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {[
              { name: "province", label: t("product:form.location.province", "Province") },
              { name: "district", label: t("product:form.location.district", "District") },
              { name: "sector", label: t("product:form.location.sector", "Sector") },
              { name: "cell", label: t("product:form.location.cell", "Cell") },
            ].map(({ name, label }) => (
              <Input
                key={name}
                type="text"
                name={name}
                value={form[name]}
                onChange={handleInputChange}
                placeholder={label}
              />
            ))}
          </div>
        </div>

        {/* Actions - Mobile: Post | Save Draft */}
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-neutral-900 border-t border-gray-200 dark:border-gray-700 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:hidden">
          <div className="max-w-2xl mx-auto grid grid-cols-2 gap-3">
            <Button
              type="submit"
              variant="primary"
              disabled={uploading || isSubmitting || submitSuccess}
              className="w-full py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-md hover:shadow-lg disabled:from-gray-400 disabled:to-gray-500 text-sm"
            >
              {submitSuccess ? (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  <span>{t("product:form.actions.posted", "Posted!")}</span>
                </>
              ) : uploading || isSubmitting ? (
                <>
                  <div className="orrith-loader orrith-loader-sm orrith-loader-on-dark"></div>
                  <span>{t("product:form.actions.uploading", "Uploading...")}</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  <span>{t("product:form.actions.listProduct", "List Product")}</span>
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                try {
                  if (!canUsePreferencesStorage()) {
                    setSubmitError(t("product:form.errors.draftSaveFailed", "Could not save draft"));
                    return;
                  }
                  localStorage.setItem(draftKey, JSON.stringify(form));
                  setSubmitError(null);
                  toast.success(t("product:form.actions.draftSaved", "Draft saved to device"));
                } catch (err) {
                  errorDev("Operation failed:", err);
                  setSubmitError(t("product:form.errors.draftSaveFailed", "Could not save draft"));
                }
              }}
              className="w-full py-3 border-2 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/10 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/20 text-sm"
            >
              <Calendar className="w-5 h-5" />
              <span>{t("product:form.actions.saveDraft", "Save Draft")}</span>
            </Button>
          </div>
        </div>

        {/* Actions - Desktop: Post | Save Draft | Clear */}
        <div className="hidden md:block">
          <div className="max-w-2xl mx-auto flex gap-3">
            <Button
              type="submit"
              variant="primary"
              disabled={uploading || isSubmitting || submitSuccess}
              className="w-full flex-1 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-md hover:shadow-lg disabled:from-gray-400 disabled:to-gray-500 text-sm"
            >
              {submitSuccess ? (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  <span>{t("product:form.actions.posted", "Posted!")}</span>
                </>
              ) : uploading || isSubmitting ? (
                <>
                  <div className="orrith-loader orrith-loader-sm orrith-loader-on-dark"></div>
                  <span>{t("product:form.actions.uploading", "Uploading...")}</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  <span>{t("product:form.actions.listProduct", "List Product")}</span>
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                try {
                  if (!canUsePreferencesStorage()) {
                    setSubmitError(t("product:form.errors.draftSaveFailed", "Could not save draft"));
                    return;
                  }
                  localStorage.setItem(draftKey, JSON.stringify(form));
                  setSubmitError(null);
                  toast.success(t("product:form.actions.draftSaved", "Draft saved to device"));
                } catch (err) {
                  errorDev("Operation failed:", err);
                  setSubmitError(t("product:form.errors.draftSaveFailed", "Could not save draft"));
                }
              }}
              className="w-full flex-1 py-3 border-2 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/10 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/20 text-sm"
            >
              <Calendar className="w-5 h-5" />
              <span>{t("product:form.actions.saveDraft", "Save Draft")}</span>
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                if (
                  !form.title &&
                  !form.desc &&
                  !form.price &&
                  !form.category &&
                  previewURLs.length === 0
                ) {
                  return;
                }
                setConfirmClearOpen(true);
              }}
              className="w-full flex-1 py-3 border-2 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20 text-sm"
            >
              <X className="w-5 h-5" />
              <span>{t("product:form.actions.clear", "Clear")}</span>
            </Button>
          </div>
        </div>

        <div className="h-28 md:h-0" />
      </form>

      {submitError && errorDialogOpen && (
        <ErrorModal
          title={t("errors:title", "Error")}
          message={submitError}
          actionLabel={t("common:close", "Close")}
          onClose={() => {
            setErrorDialogOpen(false);
            setSubmitError(null);
          }}
          onAction={() => {
            setErrorDialogOpen(false);
            setSubmitError(null);
          }}
          isDangerous
        />
      )}

      {showPricingGuide && (
        <div className="fixed inset-0 z-[1300] bg-black/45 backdrop-blur-[1px] flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-xl bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-neutral-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t("product:form.pricing.guideTitle", "Pricing guide")}
              </h3>
              <button
                type="button"
                onClick={() => setShowPricingGuide(false)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-600 dark:text-gray-300"
                aria-label={t("common:close", "Close")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-4 py-4 space-y-3 text-sm text-gray-700 dark:text-gray-300">
              <p>{t("product:form.pricing.guideLine1", "Enter the price for one kilogram in RWF.")}</p>
              <p>{t("product:form.pricing.guideLine2", "Enter the total kilograms available as quantity.")}</p>
              <p>{t("product:form.pricing.guideLine3", "Total price is calculated as: price per kg × kg amount.")}</p>
              <p>{t("product:form.pricing.guideLine4", "Only price per kg and quantity are stored. Total price is calculated when needed.")}</p>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmClearOpen}
        title={t("product:form.clearDialog.title", "Clear all data?")}
        message={t("product:form.clearDialog.message", "This action cannot be undone.")}
        confirmLabel={t("product:form.clearDialog.confirm", "Clear")}
        cancelLabel={t("common:cancel", "Cancel")}
        destructive
        onCancel={() => setConfirmClearOpen(false)}
onConfirm={() => {
           previewURLs.forEach((url) => URL.revokeObjectURL(url));
           setForm({
             title: "",
             desc: "",
             price: "",
             quantity: "",
             category: "",
             productState: PRODUCT_STATES.FRESH,
             province: "",
             district: "",
             sector: "",
             cell: "",
             expiryDate: "",
             isPrivate: false,
             quantityUnit: "kg",
             unitType: "kg",
             traySize: "",
             collectionDate: "",
             productType: "crop",
           });
           setImages([]);
           setLocalFiles([]);
           setPreviewURLs([]);
           setCoverIndex(0);
           setProductStateOptions([]);
           setStorageCondition(STORAGE_CONDITIONS.AMBIENT);
           setSubmitError(null);
           setConfirmClearOpen(false);
         }}
      />
     </div>
    </div>
  );
}